// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
// SPDX-License-Identifier: AGPL-3.0-only
// Works for both camelCase and snake_case, i.e. in the format of ${attributeName} or ${profile.attributes.attributeName} or
// in snake case like ${first_name} or ${profile.attributes.first_name} and we want to convert it to a more readable format like 'First Name' or 'Personal Administrative Number'
// ${firstName} => First Name
// ${profile.attributes.firstName} => First Name
// ${profile.attributes.personal_administrative_number} => Personal Administrative Number
export var getAttributeLabel = function (displayName) {
    var _a;
    if (displayName === null || displayName === void 0 ? void 0 : displayName.includes("$")) {
        var rawName = (_a = displayName
            // Step 1: Remove '${' from the start and '}' from the end
            .replace(/^\${|}$/g, "")
            // Step 2: Remove any leading or trailing whitespace
            .trim()
            // Step 3: Get the word after the last dot if it exists, otherwise use the whole string
            .split(".")
            .pop()) !== null && _a !== void 0 ? _a : "";
        return (rawName
            // Step 4 : Replace underscores with spaces
            .replace(/_/g, " ")
            // Step 5 : Add a space between a lowercase letter followed by an uppercase letter
            .replace(/([a-z])([A-Z])/g, "$1 $2")
            // Step 6: Capitalize the first letter and every letter after a space
            .replace(/\b\w/g, function (match) { return match.toUpperCase(); }));
    }
    return displayName !== null && displayName !== void 0 ? displayName : "";
};
export var getTranslationLabel = function (name, displayName, t) {
    if (name) {
        var key = "usersAndRolesScreen.users.fields.".concat(name);
        var translated = t(key);
        if (translated !== key) {
            return translated;
        }
    }
    return getAttributeLabel(displayName !== null && displayName !== void 0 ? displayName : "");
};
export var userBasicInfo = ["first_name", "last_name", "email", "username"];
var alwaysVisibleAdminAttributes = ["username"];
export var formatUserAtributes = function (attributes) {
    var newUserAttributesObject = {};
    if (attributes) {
        Object.entries(attributes).forEach(function (_a) {
            var key = _a[0], value = _a[1];
            if (key !== "tenant-id") {
                newUserAttributesObject["".concat(key)] = value;
            }
        });
        return newUserAttributesObject;
    }
    return null;
};
var parseJson = function (value) {
    try {
        return JSON.parse(value);
    }
    catch (_a) {
        return undefined;
    }
};
/**
 * Descriptions Keycloak stores for the options of a `select` or
 * `multiselect-checkboxes` user profile attribute. A checkbox attribute takes
 * its options from this map's keys, so an entry whose description is unusable
 * keeps the option and describes it with itself, the way the login theme's
 * `inputOptionLabels[option]!option` default does.
 *
 * Keycloak's admin console writes annotation values as strings, so a map
 * configured there arrives JSON encoded while one written straight into the
 * realm configuration arrives as an object. Indexing the encoded form by an
 * option reads a character out of the JSON text rather than a description, so
 * it is decoded first.
 */
export var getInputOptionLabels = function (attribute) {
    var annotations = attribute.annotations;
    var configured = annotations === null || annotations === void 0 ? void 0 : annotations.inputOptionLabels;
    var decoded = typeof configured === "string" ? parseJson(configured) : configured;
    if (typeof decoded !== "object" || decoded === null || Array.isArray(decoded)) {
        return undefined;
    }
    var labels = {};
    Object.entries(decoded).forEach(function (_a) {
        var option = _a[0], label = _a[1];
        labels[option] = typeof label === "string" && label.length > 0 ? label : option;
    });
    return Object.keys(labels).length > 0 ? labels : undefined;
};
/**
 * Text shown for one option description. Keycloak writes a description meant to
 * be localized as ${key}, the same convention display names use, so the key is
 * looked up before falling back to a readable form of it. Anything else is left
 * exactly as configured.
 */
export var resolveOptionLabel = function (label, t) {
    var trimmed = label.trim();
    var placeholder = /^\$\{(.+)\}$/.exec(trimmed);
    if (!placeholder) {
        return trimmed;
    }
    var translated = t(placeholder[1]);
    return translated !== placeholder[1] ? translated : getAttributeLabel(trimmed);
};
/**
 * Description configured for one option of a `select` user profile attribute:
 * the label Keycloak stores for it, or a localization override of the option
 * itself, which is how this form has always let an option be given a readable
 * name. Returns undefined when the option is left undescribed.
 */
var getSelectOptionDescription = function (optionLabels, option, t) {
    var optionLabel = optionLabels === null || optionLabels === void 0 ? void 0 : optionLabels[option];
    if (optionLabel && optionLabel !== option) {
        return resolveOptionLabel(optionLabel, t);
    }
    var translated = t(option);
    return translated !== option ? translated : undefined;
};
/**
 * Label shown for one option of a `select` user profile attribute. A configured
 * description replaces the stored value; the value is the fallback when the
 * option has no description.
 */
export var getSelectOptionLabel = function (optionLabels, option, t) {
    var description = getSelectOptionDescription(optionLabels, option, t);
    return description !== null && description !== void 0 ? description : option;
};
var toPositiveInteger = function (value) {
    var parsed = typeof value === "string" ? Number(value) : value;
    return typeof parsed === "number" && Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};
/**
 * The character bounds Keycloak's `length` validator puts on an attribute, or
 * undefined when it bounds nothing. Only the validator is read: the
 * `inputTypeMaxlength` annotation caps typing on the voter-facing forms but is
 * not enforced on submit, so stating it here would claim a rule the server does
 * not apply.
 */
export var getAttributeLengthBounds = function (attribute) {
    var validations = attribute.validations;
    // Narrowed before any bound is read off it: the jsonb can hold anything,
    // and a string or an array there would answer `.length` with a number.
    var length = validations === null || validations === void 0 ? void 0 : validations.length;
    if (typeof length !== "object" || length === null || Array.isArray(length)) {
        return undefined;
    }
    var min = toPositiveInteger(length.min);
    var max = toPositiveInteger(length.max);
    if (min === undefined && max === undefined) {
        return undefined;
    }
    // Keycloak measures the trimmed value unless the attribute opts out, so the
    // form has to measure the same string the server will.
    var trimDisabled = length["trim-disabled"];
    return { min: min, max: max, trim: !(trimDisabled === true || trimDisabled === "true") };
};
/**
 * Which bound a value breaks, or undefined when it breaks none. An absent value
 * is a matter for the attribute being required, which the form states
 * separately, so a minimum only bounds a value that is actually there.
 */
var getAttributeLengthViolation = function (bounds, value) {
    if (!bounds) {
        return undefined;
    }
    var measured = bounds.trim ? value.trim() : value;
    if (measured.length === 0) {
        return undefined;
    }
    if (bounds.min !== undefined && measured.length < bounds.min) {
        return "tooShort";
    }
    if (bounds.max !== undefined && measured.length > bounds.max) {
        return "tooLong";
    }
    return undefined;
};
/**
 * What a touched field has to report, or undefined when it has nothing to
 * report. An attribute that is required and left empty reports that rather than
 * a bound, since an empty value breaks no bound.
 */
export var getAttributeViolation = function (bounds, value, required) {
    var measured = (bounds === null || bounds === void 0 ? void 0 : bounds.trim) === false ? value : value.trim();
    if (required && measured.length === 0) {
        return "required";
    }
    return getAttributeLengthViolation(bounds, value);
};
/**
 * Whether an attribute should be hidden in the admin portal.
 *
 * This is Sequent's own annotation rather than a Keycloak one: Keycloak stores
 * and returns it without acting on it, and the login theme reads it to keep the
 * attribute off the voter-facing forms. Username is the exception: hiding it at
 * login must not remove Keycloak's built-in identifier from admin lists and
 * forms. Other attributes are read exactly as the theme reads them: the theme
 * matches the literal string, which is what Keycloak's admin console writes,
 * and a realm configuration can carry the boolean instead.
 */
export var isHiddenAttribute = function (attribute) {
    if (attribute.name && alwaysVisibleAdminAttributes.includes(attribute.name)) {
        return false;
    }
    var annotations = attribute.annotations;
    var hidden = annotations === null || annotations === void 0 ? void 0 : annotations.hidden;
    return hidden === true || hidden === "true";
};
export var isSecretAttribute = function (attribute) {
    var annotations = attribute.annotations;
    var secret = annotations === null || annotations === void 0 ? void 0 : annotations["sequent.secret"];
    return secret === true || (typeof secret === "string" && secret.toLowerCase() === "true");
};
export var getConfiguredSecretAttributeNames = function (attributes) {
    var _a;
    return (_a = attributes === null || attributes === void 0 ? void 0 : attributes.filter(isSecretAttribute).flatMap(function (attribute) { return (attribute.name ? [attribute.name] : []); })) !== null && _a !== void 0 ? _a : [];
};
/**
 * The largest maximum worth stating under a field. Keycloak's own base
 * attributes carry maxima in the hundreds as scaffolding rather than as a rule
 * anyone types into, and clients tend to copy that for free-text attributes, so
 * stating them would put a hint under most of a voter form for a bound nobody
 * reaches.
 */
var STATED_MAXIMUM_LENGTH = 100;
/**
 * The bounds worth stating under a field, or undefined when none of them are.
 * The field is still checked against everything the validator sets: this only
 * decides what is worth saying up front.
 */
export var getStatedLengthBounds = function (bounds) {
    if (!bounds) {
        return undefined;
    }
    // A minimum of one says only that the value is present, which the field
    // already says by being required or not.
    var min = bounds.min !== undefined && bounds.min > 1 ? bounds.min : undefined;
    var max = bounds.max !== undefined && bounds.max <= STATED_MAXIMUM_LENGTH ? bounds.max : undefined;
    return min === undefined && max === undefined ? undefined : { min: min, max: max };
};
